import { PaymentRequisiteToType } from '@/entities/paymentRequisit'
import { formatCardNumber, formatPhoneNumber, RequisiteModel } from '@/features/requisites'
import { BaseDialog, BaseSelect } from '@/shared/components'
import { BankIcon } from '@/shared/components/icons/banks/BankIcon'
import { SearchInput } from '@/shared/components/SearchInput/SearchInput'
import { Skeleton } from '@/shared/components/shadcn'
import { OptionModel } from '@/shared/lib/types'
import { Checkbox } from '@headlessui/react'
import { Check, X } from 'lucide-react'
import { useState } from 'react'

const detailFilterButtons: OptionModel[] = [
  { label: 'Все', value: 'all', index: 0 },
  { label: 'Активные', value: 'active', index: 1 },
  { label: 'Приостановленые', value: 'disabled', index: 2 },
  { label: 'Заблокированные', value: 'blocked', index: 4 },
]

export const DialogAttachRequisitToFarm = ({
  requisites,
  isVisible,
  onClose,
  onSearch,
  selectedStatus,
  setSelectedStatus,
  onAttach,
  isLoading,
}: {
  requisites: RequisiteModel[]
  isVisible: boolean
  onClose: () => void
  onSearch: (value: string) => void
  selectedStatus: number | string | null
  setSelectedStatus: (value: number | string | null) => void
  onAttach: (selectedIds: string[]) => void
  isLoading: boolean
}) => {
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const allSelected = requisites.length > 0 && selectedIds.length === requisites.length

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds([])
    } else {
      setSelectedIds(requisites?.map((r) => r.id) ?? [])
    }
  }

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]))
  }

  return (
    <BaseDialog
      isVisible={isVisible}
      onClose={onClose}
      title="Прикрепите реквизиты"
      closeButtonName="Отмена"
      closeButton
      actionButtonName={`Прикрепить (${selectedIds.length})`}
      onAction={() => onAttach(selectedIds)}
      isLoading={requisites.length === 0 || isLoading || selectedIds.length === 0}
    >
      <div className="flex items-center gap-2 w-full md:w-auto">
        <div className="flex-1 md:w-50 min-w-[150px]">
          <BaseSelect
            placeholder="Изменить статус"
            options={detailFilterButtons}
            value={selectedStatus}
            onChange={setSelectedStatus}
          />
        </div>
        <SearchInput
          onChange={(event) => {
            const numericOnly = event.target.value.replace(/\D/g, '')
            onSearch(numericOnly)
          }}
          minWidth="150px"
          className="flex-1"
        />
      </div>
      <div className="flex items-center gap-6">
        <Checkbox
          checked={allSelected}
          onChange={toggleSelectAll}
          className="group block bg-white data-[checked]:bg-primary data-[checked]:dark:bg-primary-dark dark:bg-transparent p-1 border border-card-border data-[checked]:border-none rounded-md ring-inset size-6"
        >
          <Check className="invisible group-data-[checked]:visible fill-black size-4 text-white dark:text-dark-bg" />
        </Checkbox>
        <span className="text-text-secondary">Реквизит</span>
        <button
          onClick={() => setSelectedIds([])}
          className="flex items-center gap-2 text-primary dark:text-primary-dark cursor-pointer"
        >
          <X className="size-5" />
          Отменить выбор
        </button>
      </div>

      <div className="h-[40vh] overflow-y-auto custom-scrollbar">
        {isLoading ? (
          Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} className="mb-2 w-full h-12" />
          ))
        ) : requisites.length === 0 ? (
          <p>Нет доступных реквизитов.</p>
        ) : (
          requisites.map((requisit) => {
            const id = requisit.id

            const paymentRequisit =
              requisit?.paymentRequisitProfiles?.length === 0 ||
              !requisit?.paymentRequisitProfiles?.[0]?.requisitType
                ? ''
                : requisit?.paymentRequisitProfiles?.[0]?.requisitType ===
                    PaymentRequisiteToType.SbpPhoneNumber
                  ? formatPhoneNumber(requisit?.paymentRequisitProfiles?.[0].value ?? '').formatted
                  : formatCardNumber(requisit?.paymentRequisitProfiles?.[0].value ?? '').formatted

            return (
              <div key={id} className="flex items-center gap-6 not-first:pt-2 pb-2 border-b">
                <Checkbox
                  checked={selectedIds.includes(id)}
                  onChange={() => toggleOne(id)}
                  className="group block bg-white data-[checked]:bg-primary data-[checked]:dark:bg-primary-dark dark:bg-transparent p-1 border border-card-border data-[checked]:border-none rounded-md ring-inset size-6"
                >
                  <Check className="invisible group-data-[checked]:visible fill-black size-4 text-white dark:text-dark-bg" />
                </Checkbox>
                <div className="flex items-center gap-3">
                  <BankIcon
                    bankId={requisit.bankId}
                    status={
                      requisit.isBlocked
                        ? 'stopped'
                        : requisit.isDeleted
                          ? 'canceled'
                          : requisit.isDisabledByUser
                            ? 'stopped'
                            : 'active'
                    }
                    className="w-[40px] h-[40px]"
                  />
                  <div className="flex flex-col">
                    <span className="font-medium text-base">
                      {`${requisit.personSurname ?? ''} ${requisit.personName ?? ''} ${requisit.personMiddlename ?? ''}`}
                    </span>
                    <span className="text-text-secondary text-sm">{paymentRequisit}</span>
                  </div>
                </div>
              </div>
            )
          })
        )}
      </div>
    </BaseDialog>
  )
}
